/**
 * Normalised shape of an API error response.
 *
 * The backend returns errors in a few slightly different shapes depending on
 * the endpoint. This interface captures the common variants, with `messages`
 * being the list of human-readable messages we want to surface to the user.
 */
export interface ApiErrorBody {
  /** Legacy single-message field (sometimes returned as an array). */
  Error?: string | string[];
  /** Optional top-level error message. */
  message?: string;
  /** Optional list of validation errors. */
  errors?: string[] | Record<string, string[]>;
  /** Anything else the server included. */
  [key: string]: unknown;
}

export interface ApiErrorResponse<T = ApiErrorBody> {
  status: number;
  statusText: string;
  /** Convenience: the first human-readable error message. */
  message: string;
  body: T;
}

/**
 * Extract a single, human-readable error message from an arbitrary
 * error body. Falls back to a generic message when nothing usable is
 * found so the UI never has to display `[object Object]`.
 */
export function extractApiErrorMessage(body: unknown, fallback = 'Something went wrong'): string {
  if (!body || typeof body !== 'object') {
    return fallback;
  }
  const b = body as ApiErrorBody;

  if (Array.isArray(b.Error) && b.Error.length) {
    return b.Error[0];
  }
  if (typeof b.Error === 'string' && b.Error) {
    return b.Error;
  }
  if (typeof b.message === 'string' && b.message) {
    return b.message;
  }
  if (Array.isArray(b.errors) && b.errors.length) {
    return b.errors[0];
  }
  if (b.errors && typeof b.errors === 'object') {
    const first = Object.values(b.errors).find((v) => Array.isArray(v) && v.length);
    if (first) {
      return (first as string[])[0];
    }
  }
  return fallback;
}
